import { call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import {
  createFollowUpRequest,
  createFollowUpSuccess,
  createFollowUpFailure,
  updateFollowUpRequest,
  updateFollowUpSuccess,
  updateFollowUpFailure,
  getFollowUpRequest,
  getFollowUpSuccess,
  getFollowUpFailure,
  getFollowUpsByDateRequest,
  getFollowUpsByDateSuccess,
  getFollowUpsByDateFailure,
  getDistinctFollowupTitlesRequest,
  getDistinctFollowupTitlesSuccess,
  getDistinctFollowupTitlesFailure,
  getFollowUpByIdRequest,
  getFollowUpByIdSuccess,
  getFollowUpByIdFailure,
  getFollowUpCountsRequest,
  getFollowUpCountsSuccess,
  getFollowUpCountsFailure,
} from './followupSlice';
import dayjs from 'dayjs';

const sanitizeNotesField = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;
  const cleaned = { ...payload };
  const raw = cleaned.Notes;
  let noteText = '';

  if (typeof raw === 'string') {
    noteText = raw.trim();
  } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    noteText = String(raw.note || '').trim();
  }

  if (noteText) {
    cleaned.Notes = noteText;
  } else {
    delete cleaned.Notes;
  }

  return cleaned;
};

// Create Data of FollowUp
function* handleCreateFollowUp(action) {
  try {
    console.log('Payload sent to API:', action.payload);

    const cleanedPayload = sanitizeNotesField(action.payload);
    if (
      typeof cleanedPayload.comment !== 'string' ||
      !cleanedPayload.comment.trim()
    ) {
      delete cleanedPayload.comment;
    } else {
      cleanedPayload.comment = cleanedPayload.comment.trim();
    }

    const response = yield call(() =>
      axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/follow-up/create-follow-up`,
        cleanedPayload,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      ),
    );

    console.log('API Response:', response); // full Axios response
    console.log('Response data:', response.data); // server data

    yield put(createFollowUpSuccess(response.data));
    if (action.payload?.leadID) {
      yield put(getFollowUpRequest(action.payload.leadID));
    }
  } catch (error) {
    console.error('API Error:', error);
    yield put(
      createFollowUpFailure(
        error.response?.data?.message || 'Something went wrong',
      ),
    );
  }
}

// Update or Edit data of FollowUp
function* updateFollowUpSaga(action) {
  try {
    const { id, data } = action.payload;
    const { leadID, ...rest } = data || {};
    const body = sanitizeNotesField(rest);
    if (typeof body.comment !== 'string' || !body.comment.trim()) {
      delete body.comment;
    } else {
      body.comment = body.comment.trim();
    }

    const response = yield call(() =>
      axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/follow-up/update-follow-up?id=${id}`,
        body,
        { headers: { 'Content-Type': 'application/json' } },
      ),
    );
    yield put(updateFollowUpSuccess(response.data));
    if (leadID) {
      yield put(getFollowUpRequest(leadID));
    }
  } catch (error) {
    yield put(updateFollowUpFailure(error.message));
  }
}

// Fetch or to get data of FollowUp
function* fetchFollowUpSaga(action) {
  try {
    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/follow-up/get-follow-up?id=${action.payload}`,
      ),
    );

    yield put(getFollowUpSuccess(response.data));
  } catch (error) {
    yield put(getFollowUpFailure(error.response?.data || error.message));
  }
}

function* fetchFollowUpsByLead(action) {
  try {
    const {
      page = 0,
      limit = 10,
      search = '',
      filters,
      type,
      startDate,
      endDate,
    } = action.payload || {};
    const params = { page, limit };
    if (search) params.search = search;
    if (type) params.type = type;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const filterList = (Array.isArray(filters) ? filters : [])
      .map((item) => ({
        status: item?.status,
        title: (Array.isArray(item?.title) ? item.title : [])
          .map((t) => String(t).trim())
          .filter(Boolean),
      }))
      .filter((item) => item.status && item.title.length > 0);

    if (filterList.length > 0) {
      params.filters = JSON.stringify(filterList);
    }

    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/follow-up/by-lead`,
        { params },
      ),
    );

    yield put(getFollowUpsByDateSuccess(response.data));
  } catch (error) {
    console.error('Fetch follow-ups error:', error);
    yield put(getFollowUpsByDateFailure(error.response?.data || error.message));
  }
}

function* fetchDistinctFollowupTitles(action) {
  try {
    const { type } = action.payload || {};
    const params = {};
    if (type) params.type = type;

    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/follow-up/followupsTitles`,
        { params },
      ),
    );

    console.log('Distinct follow-up titles:', response.data);
    yield put(getDistinctFollowupTitlesSuccess(response.data));
  } catch (error) {
    console.error('Fetch distinct titles error:', error);
    yield put(
      getDistinctFollowupTitlesFailure(error.response?.data || error.message),
    );
  }
}

function* fetchFollowUpByIdSaga(action) {
  try {
    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/follow-up/followupById`,
        { params: { id: action.payload } },
      ),
    );

    yield put(getFollowUpByIdSuccess(response.data));
  } catch (error) {
    console.error('Fetch follow-up by id error:', error);
    yield put(getFollowUpByIdFailure(error.response?.data || error.message));
  }
}

function* fetchFollowUpCountsSaga() {
  try {
    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/follow-up/get-follow-up-counts`,
      ),
    );

    yield put(getFollowUpCountsSuccess(response.data));
  } catch (error) {
    console.error('Fetch follow-up counts error:', error);
    yield put(getFollowUpCountsFailure(error.response?.data || error.message));
  }
}

export default function* followUpSaga() {
  yield takeLatest(createFollowUpRequest.type, handleCreateFollowUp);
  yield takeLatest(updateFollowUpRequest.type, updateFollowUpSaga);
  yield takeLatest(getFollowUpRequest.type, fetchFollowUpSaga);
  yield takeLatest(getFollowUpsByDateRequest.type, fetchFollowUpsByLead);
  yield takeLatest(
    getDistinctFollowupTitlesRequest.type,
    fetchDistinctFollowupTitles,
  );
  yield takeLatest(getFollowUpByIdRequest.type, fetchFollowUpByIdSaga);
  yield takeLatest(getFollowUpCountsRequest.type, fetchFollowUpCountsSaga);
}
